import { PhotoVerificationAttemptDetailDto } from '@ivy-monorepo/shared'
import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import Avatar from '../Avatar'
import Icon from '../Icon'
import {
  formatPhotoVerificationFaceIssues,
  formatPhotoVerificationFailureReason,
  getPhotoVerificationAvatarCaption,
} from '../../helpers/photo-verification'

interface PhotoVerificationAttemptDetailModalProps {
  selected: PhotoVerificationAttemptDetailDto | null
  detailLoading: boolean
  verifying: boolean
  actionError: string | null
  showUserLink?: boolean
  onClose: () => void
  onManualVerify: () => void
}

const PhotoVerificationAttemptDetailModal = ({
  selected,
  detailLoading,
  verifying,
  actionError,
  showUserLink = true,
  onClose,
  onManualVerify,
}: PhotoVerificationAttemptDetailModalProps) => {
  return (
    <Modal
      isOpen={Boolean(selected) || detailLoading}
      onRequestClose={onClose}
      className="modal-popup photo-verification-modal"
      overlayClassName="modal-overlay"
      contentLabel="Photo verification attempt"
    >
      <div className="modal-content-wrapper">
        <div className="modal-body-content">
          {detailLoading && !selected && <p>Loading…</p>}
          {selected && (
            <>
              <h3>
                {selected.firstName} {selected.lastName}
              </h3>

              <div className="photo-verification-detail-layout">
                <div className="photo-verification-detail-photo">
                  {selected.currentImageUrl ? (
                    <img src={selected.currentImageUrl} alt="Current avatar" />
                  ) : (
                    <>
                      <div className="photo-verification-detail-photo-avatar">
                        <Avatar
                          firstName={selected.firstName}
                          lastName={selected.lastName}
                          className="extra-large"
                        />
                      </div>
                      <p className="photo-verification-detail-photo-caption">
                        {getPhotoVerificationAvatarCaption(selected)}
                      </p>
                    </>
                  )}
                </div>

                <div className="photo-verification-detail-stats">
                  <dl>
                    <div>
                      <dt>Context</dt>
                      <dd>{selected.context}</dd>
                    </div>
                    <div>
                      <dt>Result</dt>
                      <dd>
                        {selected.manuallyVerifiedAt
                          ? 'Manually verified'
                          : selected.passed
                            ? 'Passed'
                            : formatPhotoVerificationFailureReason(selected.failureErrorCode)}
                      </dd>
                    </div>
                    <div>
                      <dt>Qualifying face</dt>
                      <dd>{selected.visionResult.hasQualifyingFace ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>Face quality issues</dt>
                      <dd>{formatPhotoVerificationFaceIssues(selected.visionResult.faceQualityIssues)}</dd>
                    </div>
                    <div>
                      <dt>Adult SafeSearch</dt>
                      <dd>{selected.visionResult.adult ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Adult content absent</dt>
                      <dd>
                        {selected.visionResult.isAdultContentAbsent == null
                          ? '—'
                          : selected.visionResult.isAdultContentAbsent
                            ? 'Yes'
                            : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt>Likely minor</dt>
                      <dd>
                        {selected.visionResult.isLikelyMinor
                          ? `Yes (${selected.visionResult.minorLabels.join(', ') || 'labels'})`
                          : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt>Person objects</dt>
                      <dd>
                        {selected.visionResult.persons.length === 0
                          ? 'None'
                          : selected.visionResult.persons
                              .map((person: { name: string; score: number }) => `${person.name} (${person.score.toFixed(2)})`)
                              .join(', ')}
                      </dd>
                    </div>
                    <div>
                      <dt>Matches current avatar</dt>
                      <dd>{selected.isCurrentAvatar ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>User photo verified</dt>
                      <dd>{selected.userIsPhotoVerified ? 'Yes' : 'No'}</dd>
                    </div>
                    {selected.manuallyVerifiedAt && (
                      <div>
                        <dt>Manually verified at</dt>
                        <dd>{new Date(selected.manuallyVerifiedAt).toLocaleString()}</dd>
                      </div>
                    )}
                    {selected.manuallyVerifiedByEmail && (
                      <div>
                        <dt>Manually verified by</dt>
                        <dd>{selected.manuallyVerifiedByEmail}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Attempt created</dt>
                      <dd>{new Date(selected.createdAt).toLocaleString()}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {actionError && <div className="photo-verification-error">{actionError}</div>}
            </>
          )}
        </div>

        <div className="modal-footer-content">
          {showUserLink && selected && (
            <Link
              to={`/admin/users/${selected.userPublicId}`}
              className="styled-button photo-verification-user-profile-link"
            >
              <Icon name="ArrowUpRightFromSquare" />
              Open user profile
            </Link>
          )}
          <button type="button" className="styled-button" onClick={onClose} disabled={verifying}>
            Close
          </button>
          {selected?.canManuallyVerify && (
            <button
              type="button"
              className="styled-button"
              onClick={onManualVerify}
              disabled={verifying}
            >
              {verifying ? 'Verifying…' : 'Manually verify'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PhotoVerificationAttemptDetailModal
